import { modelOptions, mongoose, post, prop, type Ref } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    OwnerMostRecentDataFieldsWithDefaults,
    OwnerMostRecentDataSchemaTypes,
} from "../types/ownerMostRecentData.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "OwnerMostRecentData" as const;

@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: SCHEMA_KEY,
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<OwnerMostRecentData>>(...addSchemaKeyHook(SCHEMA_KEY))
export class OwnerMostRecentData extends SchemaBase<OwnerMostRecentData, OwnerMostRecentDataFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ ref: () => Organization, unique: true, required: true })
    public owner!: Ref<Organization>;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public updatedAt?: Date;

    // The only writer passes a number, so coercing `value` may be unnecessary — worth evaluating.
    @validate({
        zodValidator: z.record(
            z.string(),
            z.object({
                value: z.coerce.number(),
                date: z.coerce.date(),
            }),
        ),
        hasDefault: true,
    })
    @prop({ type: () => Object, required: true, default: {} })
    public data: Record<string, { value: number; date: Date }> = {};

    static getters = {} as const satisfies Record<string, (self: OwnerMostRecentData) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<OwnerMostRecentDataSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<OwnerMostRecentData, OwnerMostRecentDataFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

OwnerMostRecentData.dtoValidators = getZodSchemaFromClass(
    OwnerMostRecentData,
) as unknown as DtoValidatorTypes<OwnerMostRecentDataSchemaTypes>;
